import { DatePipe } from '@angular/common';
import { inject, Injectable, isDevMode, signal } from '@angular/core';
import { FileDocument } from '@lib/models/file-document.model';
import {
  AvancementConnaissanceClientFragmentFragment,
  DetailedInvestisseurFragment,
  FileMetadata,
  GedDocumentType,
  GenerateRecueilGQL,
  GenerationRecueilTypeEnum,
  Investisseur,
  SignatureState,
  StorageFile,
} from '@lib/models/generated/graphql';
import { ConsultantPreferencesService } from '@lib/services/consultant-preferences.service';
import { DocumentViewerOpenOptions, DocumentViewerPanelService, RecueilTypePreference } from '@lib/services/document-viewer-panel.service';
import { DocumentsService } from '@lib/services/documents.service';
import { InvestisseurService } from '@lib/services/investisseur.service';
import { LoadingService } from '@lib/services/loading.service';
import { getRawFileId, toDateOrUndefined } from '@lib/utils/app-utils';
import { QueryManagerService } from 'finzzle-angular-utils';
import { distinctUntilChanged, filter, firstValueFrom, map, Observable } from 'rxjs';
@Injectable({
  providedIn: 'root',
})
export class RecueilService {
  private generateRecueilGQL = inject(GenerateRecueilGQL);
  private investisseurService = inject(InvestisseurService);
  private queryManagerService = inject(QueryManagerService);
  private documentViewerPanelService = inject(DocumentViewerPanelService);
  private consultantPreferencesService = inject(ConsultantPreferencesService);
  private loadingService = inject(LoadingService);
  private documentService = inject(DocumentsService);
  private readonly datePipe = inject(DatePipe);
  public canGenerateRecueil = signal<boolean>(false);
  private readonly recueilDocumentDescription = "Recueil d'informations";
  // Subscribe to investisseur changes to fetch recueil state when investisseur changes
  public recueilState$: Observable<AvancementConnaissanceClientFragmentFragment | null> = this.investisseurService.investisseur$.pipe(
    map<DetailedInvestisseurFragment | null, AvancementConnaissanceClientFragmentFragment | null>(
      investisseur => investisseur?.avancementConnaissanceClientInvestisseur || null
    ),
    distinctUntilChanged()
  );
  public async generateRecueil(viewerOptions: DocumentViewerOpenOptions = {}): Promise<void> {
    this.loadingService.startLoading();
    // Récupérer la préférence du consultant avant de générer
    const recueilTypePreference = this.mapGenerationRecueilTypeToPreference(this.consultantPreferencesService.generationRecueilType());
    // On s'assure d'avoir la dernière version
    await this.investisseurService.getInvestisseur(this.investisseurService.investisseurSig()!.code);
    const investisseur = this.investisseurService.investisseurSig() as Investisseur;
    const conjoint = this.investisseurService.investisseurConjointSig() as Investisseur | null;
    try {
      if (recueilTypePreference === RecueilTypePreference.seule && conjoint) {
        const [recueilInvestisseur, recueilConjoint] = await Promise.all([
          this.getRecueilMetadata(investisseur, false, 'RecueilService.generateRecueil.investisseur'),
          this.getRecueilMetadata(conjoint, false, 'RecueilService.generateRecueil.conjoint'),
        ]);
        if (recueilInvestisseur && recueilConjoint) {
          this.documentViewerPanelService.openRecueilDocumentViewer(
            recueilTypePreference,
            undefined,
            [recueilInvestisseur, recueilConjoint],
            viewerOptions
          );
        }
        return;
      }

      const recueilMetadata = await this.getRecueilMetadata(
        null,
        recueilTypePreference === RecueilTypePreference.couple,
        'RecueilService.generateRecueil'
      );
      if (recueilMetadata) {
        this.documentViewerPanelService.openRecueilDocumentViewer(recueilTypePreference, recueilMetadata, undefined, viewerOptions);
      }
    } finally {
      this.loadingService.stopLoading();
    }
  }

  public async generateRecueilForOperation(investisseurCode: string, avecCoInvestisseur: boolean): Promise<StorageFile> {
    const investor = (await this.investisseurService.fetchInvestisseurByCode(investisseurCode)) as Investisseur | null;
    if (!investor?.avancementConnaissanceClientInvestisseur?.metadatasConnaissanceClient?.id) {
      throw new Error('Recueil metadata ID is not available for this investor.');
    }

    const recueil = await this.getRecueilMetadata(investor, avecCoInvestisseur, 'RecueilService.generateRecueilForOperation');
    if (!recueil) {
      throw new Error('Unable to generate Recueil document for operation.');
    }
    return recueil;
  }

  public async loadUnsignedDocument(preloadedMetadata?: StorageFile | null): Promise<FileDocument | null> {
    const fileMetadata = preloadedMetadata ?? (await this.getRecueilMetadata(null, true, 'RecueilService.loadUnsignedDocument')) ?? null;
    if (isDevMode()) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    if (fileMetadata?.fileConnectionInfo?.sasUrl) {
      const url = await this.documentService.fetchAndCacheBlobDocument(fileMetadata.fileConnectionInfo.sasUrl, fileMetadata.fileType);
      if (url) {
        const investisseur = this.investisseurService.investisseurSig() as Investisseur;
        const conjoint = this.investisseurService.investisseurConjointSig() as Investisseur | null;
        const fileName = this.buildRecueilFileName(investisseur, conjoint, fileMetadata.lastModificationDate ?? fileMetadata.createdDate);
        return new FileDocument(
          fileName,
          fileMetadata?.lastModificationDate,
          GedDocumentType.RecInfo,
          url,
          this.recueilDocumentDescription,
          undefined,
          this.investisseurService.coupleInvestisseurIds()
        );
      }
    }
    return null;
  }
  public async LoadInvestisseurDocument(investisseur: Investisseur, preloadedMetadata?: StorageFile | null): Promise<FileDocument | null> {
    const fileMetadata =
      preloadedMetadata ?? (await this.getRecueilMetadata(investisseur, false, 'RecueilService.LoadInvestisseurDocument')) ?? null;
    if (isDevMode()) {
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
    if (fileMetadata?.fileConnectionInfo?.sasUrl) {
      const url = await this.documentService.fetchAndCacheBlobDocument(fileMetadata.fileConnectionInfo.sasUrl, fileMetadata.fileType);
      if (url) {
        const fileName = this.buildRecueilFileName(investisseur, null, fileMetadata.lastModificationDate ?? fileMetadata.createdDate);
        return new FileDocument(
          fileName,
          fileMetadata?.lastModificationDate,
          GedDocumentType.RecInfo,
          url,
          this.recueilDocumentDescription,
          fileMetadata.id,
          [investisseur.id]
        );
      }
    }
    return null;
  }
  public async loadSignedDocument(): Promise<FileDocument | null> {
    const documents = await firstValueFrom(
      this.investisseurService.investisseurDocuments$.pipe(filter((docs): docs is FileMetadata[] => docs !== null))
    );
    const investisseur = this.investisseurService.investisseurSig() as Investisseur | null;
    if (!investisseur) throw 'Unable to get investisseur for recueil generation';
    const conjoint = this.investisseurService.investisseurConjointSig() as Investisseur | null;
    return this.getSignedFile(documents, investisseur, conjoint);
  }
  public async loadConjointSignedDocument(): Promise<FileDocument | null> {
    const documents = await this.documentService.getInvestisseurDocumentsByInvestisseurId(
      this.investisseurService.investisseurConjointSig()?.id
    );
    const investisseur = this.investisseurService.investisseurSig() as Investisseur | null;
    const conjoint = this.investisseurService.investisseurConjointSig() as Investisseur | null;
    if (!conjoint) throw 'Unable to get conjoint for recueil generation';
    return this.getSignedFile(documents, conjoint, investisseur);
  }
  // Contrairement à loadSignedDocument()/loadConjointSignedDocument() (qui ne regardent que le fichier
  // actuellement pointé par recueildocumentID), cette méthode parcourt tout l'historique GED des deux
  // investisseurs pour retrouver, variante par variante (couple/solo), si un document a réellement été signé
  // à un moment donné — même si recueildocumentID pointe depuis vers un autre fichier plus récent.
  public async getSignedRecueilFiles(): Promise<FileMetadata[]> {
    const conjointId = this.investisseurService.investisseurConjointSig()?.id;
    if (!conjointId) return [];
    const [invDocs, conjointDocs] = await Promise.all([
      firstValueFrom(this.investisseurService.investisseurDocuments$.pipe(filter((docs): docs is FileMetadata[] => docs !== null))),
      this.documentService.getInvestisseurDocumentsByInvestisseurId(conjointId),
    ]);
    return [...invDocs, ...(conjointDocs ?? [])].filter(
      f =>
        f.typeDocumentCode === GedDocumentType.RecInfo &&
        (f.etatSignature === SignatureState.Completed || f.etatSignature === SignatureState.Pending)
    );
  }
  public getFakeDocument(): FileDocument {
    const investisseur = this.investisseurService.investisseurSig() as Investisseur;
    const conjoint = this.investisseurService.investisseurConjointSig() as Investisseur | null;
    const document = new FileDocument(
      this.buildRecueilFileName(investisseur, conjoint, new Date()),
      new Date(),
      GedDocumentType.RecInfo,
      undefined,
      this.recueilDocumentDescription
    );
    document.isFakeFile = true;
    return document;
  }
  private getSignedFile(
    fileMetadatas: FileMetadata[] | undefined,
    primaryInvestisseur: Investisseur,
    secondaryInvestisseur: Investisseur | null
  ): FileDocument | null {
    const signedFile = fileMetadatas
      ?.filter(
        f =>
          f.typeDocumentCode === GedDocumentType.RecInfo &&
          (f.etatSignature === SignatureState.Completed || f.etatSignature === SignatureState.Pending) &&
          getRawFileId(f.fileId) === getRawFileId(primaryInvestisseur.avancementConnaissanceClientInvestisseur?.recueildocumentID)
      )
      ?.reduce(
        (a, b) => {
          if (!a) return b;
          return new Date(a.dateDeDerniereModification) > new Date(b.dateDeDerniereModification) ? a : b;
        },
        undefined as FileMetadata | undefined
      );
    if (signedFile) {
      const fileDocument = FileDocument.fromFileMetadata(signedFile, this.recueilDocumentDescription);
      fileDocument.fileName =
        signedFile.fileName ?? this.buildRecueilFileNameFromMetadata(signedFile, primaryInvestisseur, secondaryInvestisseur);
      return fileDocument;
    }
    return null;
  }
  private async getRecueilMetadata(
    investisseur: Investisseur | null = null,
    avecCoInvestisseur: boolean = true,
    calledBy: string
  ): Promise<StorageFile | null | undefined> {
    console.log(`RecueilService.getRecueilMetadata called by: ${calledBy}`);
    const investisseurToUse = investisseur ?? this.investisseurService.investisseurSig();
    const recueilMetadataId = investisseurToUse?.avancementConnaissanceClientInvestisseur?.metadatasConnaissanceClient?.id;
    if (!recueilMetadataId) {
      console.error('recueil metadata ID is not available.');
      return null;
    }
    // Call the service to generate the recueil document
    const result = await this.queryManagerService.handleMutationErrorsAsync(
      this.generateRecueilGQL.mutate({
        fetchPolicy: 'network-only',
        variables: {
          recueilMetadataId: recueilMetadataId,
          investisseurPrincipalId: investisseurToUse?.id,
          avecCoInvestisseur: avecCoInvestisseur,
        },
      })
    );
    if (result.error || !result.data) {
      console.error('Error generating recueil:', result.error);
      return null;
    }
    return result.data.generateRecueil;
  }
  private buildRecueilFileName(investisseur: Investisseur, conjoint: Investisseur | null, date?: Date | string | null): string {
    const parts: string[] = [];
    const investisseurPart = this.formatNomPrenom(investisseur?.personnePhysique?.[0]);
    if (investisseurPart) {
      parts.push(investisseurPart);
    }
    const conjointPart = conjoint ? this.formatNomPrenom(conjoint.personnePhysique?.[0]) : null;
    if (conjointPart) {
      parts.push(conjointPart);
    }
    const datePart = this.formatRecueilDate(date);
    const base = parts.length ? `Recueil-Informations-${parts.join('&')}` : 'Recueil-Informations';
    return `${base}-${datePart}.pdf`;
  }
  private buildRecueilFileNameFromMetadata(
    fileMetadata: FileMetadata,
    primaryInvestisseur: Investisseur,
    secondaryInvestisseur: Investisseur | null
  ): string {
    const date = fileMetadata.dateDeDerniereModification ?? fileMetadata.dateDeCreation;
    return this.buildRecueilFileName(primaryInvestisseur, secondaryInvestisseur, date);
  }
  private formatNomPrenom(personne?: Investisseur['personnePhysique'][number] | null): string | null {
    if (!personne) return null;
    const nomUsage = personne.nom?.trim();
    const nomNaissance = personne.nomNaissance?.trim();
    const nom = nomUsage || nomNaissance || '';
    const prenom = personne.prenom?.trim() ?? '';
    return `${nom}_${prenom}`;
  }
  private formatRecueilDate(date?: Date | string | null): string {
    const parsedDate = toDateOrUndefined(date) ?? new Date();
    const formatted = this.datePipe.transform(parsedDate, 'dd_MM_yyyy');
    return formatted ?? this.datePipe.transform(new Date(), 'dd_MM_yyyy') ?? '';
  }
  private mapGenerationRecueilTypeToPreference(generationRecueilType: GenerationRecueilTypeEnum): RecueilTypePreference {
    if (generationRecueilType === GenerationRecueilTypeEnum.Solo) {
      return RecueilTypePreference.seule;
    }
    return RecueilTypePreference.couple;
  }
}
